import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { object, number } from 'yup';
import { useTheme } from '@mui/material/styles';
import useOrganization from '../../hooks/UseOrganization';

const keyToFieldMap = {
  session_expire_minutes: 'ssoAccessTokenLife',
  token_expire_minutes: 'accessTokenLife',
  allow_forgetpassword: 'allowForgetPassword',
  ad_login_only: 'adLoginOnly',
};

/**
 * @function LoginSubModel
 * @description Renders a form for managing LoginSubModel settings of an organization, including various details. It supports creating a new organization or editing an existing one.
 * @param {object} state - The state object containing the mode and other relevant information.
 * @param {string} [state.mode] - The mode of the form, either 'edit' or 'create'.
 * @returns {JSX} returns the UI of LoginSubModel.
 * @example <LoginSubModel state={state} />
 */

const LoginSubModel = () => {
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();
  const { updateLogin, getLogin } = useOrganization();
  const [loginData, setLoginData] = useState([]);

  const initialValues = {
    ssoAccessTokenLife: 240,
    accessTokenLife: 60,
    adLoginOnly: 'false',
    allowForgetPassword: 'true',
  };

  const validationSchema = object().shape({
    ssoAccessTokenLife: number()
      .integer('SSO access token life must be an integer')
      .required('Required')
      .min(15, 'SSO access token life must be greater than or equal to 15')
      .max(1440, 'SSO access token life must be less than or equal to 1440'),
    accessTokenLife: number()
      .integer('Access token life must be an integer')
      .required('Required')
      .min(5, 'Access token life must be greater than or equal to 5')
      .max(60, 'Access token life must be less than or equal to 60'),
  });

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const payload = loginData.map((item) => {
        const field = keyToFieldMap[item?.key];
        return {
          setting_id: item?.id,
          value: values?.[field],
        };
      });

      updateLogin(orgName, payload);
    },
  });

  useEffect(() => {
    getLogin(orgName).then((resp) => {
      const data = resp?.data;
      setLoginData(data);

      data.forEach((item) => {
        const field = keyToFieldMap[item?.key];
        if (field) {
          formikForm.setFieldValue(field, item?.value);
        }
      });
    });
  }, [orgName]);

  const handleOnChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );
  const handleOnKeyDown = useCallback(
    (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
    []
  );

  return (
    <Grid padding="20px">
      <Grid container alignItems="center" item xs={12}>
        <Typography
          variant="h3"
          color={theme.palette.text.primary}
          flexGrow="1"
        >
          Login Configuration
        </Typography>
      </Grid>
      <Grid
        container
        direction="row"
        spacing={2}
        paddingTop={2}
        xs={12}
        md={6}
        lg={4}
      >
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="SSO access token life"
            type="number"
            onKeyDown={handleOnKeyDown}
            placeholder="Enter SSO access token life in minutes "
            name="ssoAccessTokenLife"
            value={formikForm?.values?.ssoAccessTokenLife}
            onChange={handleOnChange}
            aria-label="ssoAccessTokenLife"
            onBlur={formikForm?.handleBlur}
            error={
              formikForm.touched.ssoAccessTokenLife &&
              Boolean(formikForm.errors.ssoAccessTokenLife)
            }
            helperText={
              formikForm.touched.ssoAccessTokenLife &&
              formikForm.errors.ssoAccessTokenLife
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Access token life"
            fullWidth
            type="number"
            required
            onKeyDown={handleOnKeyDown}
            placeholder="Enter access token life in minutes"
            name="accessTokenLife"
            value={formikForm?.values?.accessTokenLife}
            onChange={handleOnChange}
            aria-label="accessTokenLife"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.accessTokenLife &&
              formikForm?.touched?.accessTokenLife
                ? formikForm?.errors?.accessTokenLife
                : null
            }
            error={
              Boolean(formikForm?.errors?.accessTokenLife) &&
              formikForm?.touched?.accessTokenLife
            }
          />
        </Grid>
        <Grid container item xs={12} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="body1">Allow forget password</Typography>
          </Grid>
          <Grid item xs={12}>
            <RadioGroup
              row
              name="allowForgetPassword"
              value={formikForm?.values?.allowForgetPassword}
              label="allowForgetPassword"
              onBlur={formikForm?.handleBlur}
              onChange={formikForm.handleChange}
              helperText={
                formikForm?.errors?.allowForgetPassword &&
                formikForm?.touched?.allowForgetPassword
                  ? formikForm?.errors?.allowForgetPassword
                  : null
              }
              error={
                Boolean(formikForm?.errors?.allowForgetPassword) &&
                formikForm?.touched?.allowForgetPassword
              }
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>
        </Grid>
        <Grid container item xs={12} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="body1">AD login only</Typography>
          </Grid>
          <Grid item xs={12}>
            <RadioGroup
              row
              name="adLoginOnly"
              onChange={formikForm.handleChange}
              value={formikForm?.values?.adLoginOnly}
              label="adLoginOnly"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.adLoginOnly &&
                formikForm?.touched?.adLoginOnly
                  ? formikForm?.errors?.adLoginOnly
                  : null
              }
              error={
                Boolean(formikForm?.errors?.adLoginOnly) &&
                formikForm?.touched?.adLoginOnly
              }
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent="flex-start" mt={3}>
        <Box mr="12px">
          <Button
            variant="outlined"
            onClick={formikForm?.handleReset}
            title="Reset"
          >
            Reset
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            onClick={formikForm.handleSubmit}
            title="Submit"
          >
            Submit
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginSubModel;
